/**
 * Promos DLC courte (anti-gaspi) — pilotage.
 *
 * Les produits dont la DLC du rayon tombe dans le délai de leur rayon
 * (épicerie ~14 j, crémerie ~3-4 j — réglable ici) sont proposés ; un geste
 * les passe à −50 %. Le prix remisé est servi PARTOUT (eshop, caisse,
 * étiquettes) depuis le même endroit, et la promo s'éteint toute seule quand
 * le lot tourne ou que la DLC passe. Penser à réimprimer l'étiquette de rayon.
 *
 * Aussi rendue comme 4e onglet de Réappro, à la caisse (`integre`, 21/10/2026) :
 * sans son grand titre, l'onglet le porte déjà.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { api } from '../services/api';
import { formatQty } from '../utils/pos.utils';
import { logger } from '../utils/logger';
import RemiseParLot from '../components/RemiseParLot';
import BilanAntiGaspi from '../components/remise/BilanAntiGaspi';

export interface CandidatPromo {
  id: string;
  name: string;
  unit?: string | null;
  category_name?: string | null;
  lead_days: number;
  dlc: string;
  days_left: number;
  stock_display: number;
  price_cents: number | null;
  promo_price_cents: number | null;
  promo_active: boolean;
  promo_percent?: number | null;
}

interface Rayon {
  id: string;
  name: string;
  parent_id?: string | null;
  promo_dlc_lead_days?: number | null;
}

export default function PromoDlcPage({
  integre = false,
  onCandidats,
}: {
  /** Rendue dans un onglet (Réappro, à la caisse) : sans grand titre. */
  integre?: boolean;
  /** Prévient l'hôte de la liste chargée (pastille de l'onglet). */
  onCandidats?: (candidats: CandidatPromo[]) => void;
} = {}) {
  const [candidates, setCandidates] = useState<CandidatPromo[]>([]);
  const [rayons, setRayons] = useState<Rayon[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [leadEdits, setLeadEdits] = useState<Record<string, string>>({});
  const onCandidatsRef = useRef(onCandidats);
  onCandidatsRef.current = onCandidats;

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [cands, cats]: [CandidatPromo[], Rayon[]] = await Promise.all([
        api.get('/products/promo-dlc/candidates'),
        api.get('/categories'),
      ]);
      const liste = Array.isArray(cands) ? cands : [];
      setCandidates(liste);
      onCandidatsRef.current?.(liste);
      // Réglage au niveau des rayons (niveau 0) — hérité par les sous-catégories
      setRayons((Array.isArray(cats) ? cats : []).filter((c) => !c.parent_id));
    } catch (e) {
      logger.error('[PromoDlc] chargement échoué:', e);
      setError('Chargement impossible.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const togglePromo = async (c: CandidatPromo) => {
    setBusy(c.id);
    setError(null);
    try {
      if (c.promo_active) {
        await api.delete(`/products/${c.id}/promo-dlc`);
      } else {
        await api.post(`/products/${c.id}/promo-dlc`, {});
      }
      await load();
    } catch (e: any) {
      setError(e?.response?.data?.message || 'Échec — rien n\'a été modifié.');
    } finally {
      setBusy(null);
    }
  };

  const saveLeadDays = async (rayon: Rayon) => {
    const raw = (leadEdits[rayon.id] ?? '').trim();
    const value = raw === '' ? null : parseInt(raw, 10);
    if (raw !== '' && (!Number.isFinite(value!) || value! < 0)) return;
    setBusy(rayon.id);
    try {
      await api.patch(`/products/categories/${rayon.id}/promo-lead-days`, { lead_days: value });
      await load();
    } catch (e: any) {
      setError(e?.response?.data?.message || 'Échec du réglage.');
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        {!integre && <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">🏷️ Promos DLC courte</h1>}
        <p className={integre ? 'text-base text-gray-600' : 'mt-1 text-sm text-gray-500'}>
          Anti-gaspi : les produits dont la DLC approche passent à −50 %. Le prix remisé
          s'applique partout (eshop, caisse, étiquettes) et la promo s'éteint toute seule
          quand le lot tourne ou que la DLC est passée.
        </p>
      </div>

      {error && <p className="text-sm font-medium text-red-700">{error}</p>}

      {/* Remise par LOT : un lot remisé sans toucher au lot frais du même produit. */}
      <RemiseParLot />

      {/* Ce que la remise par lot a rapporté et coûté */}
      <BilanAntiGaspi />

      {/* Candidats */}
      <div className="bg-white rounded-lg border border-gray-200">
        <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
          <h2 className="font-semibold text-gray-800">
            Candidats ({candidates.length})
          </h2>
          <button onClick={() => void load()} className="text-sm text-blue-600">
            ↻ Actualiser
          </button>
        </div>
        {loading ? (
          <p className="text-center text-gray-500 py-8">Chargement…</p>
        ) : candidates.length === 0 ? (
          <p className="text-center text-gray-500 py-8">
            Aucun produit dans les délais — règle les délais par rayon ci-dessous si la
            liste doit se remplir.
          </p>
        ) : (
          <div className="divide-y divide-gray-100">
            {candidates.map((c) => (
              <div key={c.id} className="px-4 py-3 flex items-center gap-3 flex-wrap">
                <div className="min-w-0 flex-1">
                  <div className="font-medium text-gray-900 truncate">{c.name}</div>
                  <div className="text-xs text-gray-500">
                    {c.category_name || 'Sans rayon'} · rayon : {formatQty(c.stock_display)}{' '}
                    {c.unit || ''}
                  </div>
                </div>
                <div
                  className={`text-sm font-semibold shrink-0 ${
                    c.days_left <= 1 ? 'text-red-700' : 'text-orange-600'
                  }`}
                >
                  DLC {new Date(c.dlc).toLocaleDateString('fr-BE')} ·{' '}
                  {c.days_left === 0 ? "aujourd'hui" : `J−${c.days_left}`}
                </div>
                {c.price_cents != null && (
                  <div className="text-sm shrink-0">
                    <span className="line-through text-gray-400">
                      {(c.price_cents / 100).toFixed(2)} €
                    </span>{' '}
                    <span className="font-bold text-red-700">
                      {((c.promo_price_cents ?? 0) / 100).toFixed(2)} €
                    </span>
                  </div>
                )}
                <button
                  onClick={() => void togglePromo(c)}
                  disabled={busy === c.id}
                  className={`shrink-0 px-3 py-1.5 text-sm font-semibold rounded-lg border-2 disabled:opacity-40 ${
                    c.promo_active
                      ? 'border-gray-300 text-gray-600 active:bg-gray-50'
                      : 'border-red-600 text-red-700 active:bg-red-50'
                  }`}
                >
                  {busy === c.id ? '…' : c.promo_active ? 'Retirer la promo' : '−50 %'}
                </button>
              </div>
            ))}
          </div>
        )}
        <p className="px-4 py-2 text-xs text-gray-400 border-t border-gray-100">
          ⚠️ Après activation : réimprimer l'étiquette de rayon (elle sort au prix promo).
          Un retrait choisi APRÈS la DLC est refusé au client à la commande.
        </p>
      </div>

      {/* Délais par rayon */}
      <div className="bg-white rounded-lg border border-gray-200">
        <div className="px-4 py-3 border-b border-gray-100">
          <h2 className="font-semibold text-gray-800">Délais d'éligibilité par rayon</h2>
          <p className="text-xs text-gray-500 mt-0.5">
            Nombre de jours avant la DLC où un produit devient candidat. Vide = pas de
            suggestion pour ce rayon. Longue conservation ~14 j, frais/crémerie ~3-4 j.
          </p>
        </div>
        <div className="divide-y divide-gray-100">
          {rayons.map((r) => (
            <div key={r.id} className="px-4 py-2.5 flex items-center gap-3">
              <span className="flex-1 text-sm text-gray-800">{r.name}</span>
              <input
                type="text"
                inputMode="numeric"
                placeholder="—"
                value={leadEdits[r.id] ?? (r.promo_dlc_lead_days != null ? String(r.promo_dlc_lead_days) : '')}
                onChange={(e) => setLeadEdits((p) => ({ ...p, [r.id]: e.target.value }))}
                className="w-16 px-2 py-1.5 border border-gray-300 rounded-lg text-center text-sm"
              />
              <span className="text-xs text-gray-500 w-8">jours</span>
              <button
                onClick={() => void saveLeadDays(r)}
                disabled={busy === r.id}
                className="px-3 py-1.5 text-sm font-semibold border border-blue-500 text-blue-700 rounded-lg disabled:opacity-40"
              >
                OK
              </button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
